import indexedDBService from './indexedDB';
import backgroundSyncService from './backgroundSync';
import { getCurrentUser } from './auth';
import { Task } from '../types/task';
import { Shipping } from '../types/shipping';

interface CacheOptions {
  maxAge?: number; // Cache duration in milliseconds
  forceRefresh?: boolean;
  background?: boolean; // Whether to refresh in background
  fallbackToCache?: boolean; // Use cache if server call fails
}

interface CacheResult<T> {
  data: T;
  source: 'cache' | 'server' | 'hybrid';
  timestamp: number;
  stale: boolean;
}

interface CacheStrategy {
  name: string;
  maxAge: number;
  refreshThreshold: number; // When to refresh in background
  priority: 'cache-first' | 'server-first' | 'stale-while-revalidate';
}

class CacheManagerService {
  private readonly strategies: Record<string, CacheStrategy> = {
    tasks: {
      name: 'tasks',
      maxAge: 5 * 60 * 1000, // 5 minutes for tasks
      refreshThreshold: 2 * 60 * 1000, // Refresh in background after 2 minutes
      priority: 'stale-while-revalidate'
    },
    shipments: {
      name: 'shipments',
      maxAge: 10 * 60 * 1000, // 10 minutes for shipments
      refreshThreshold: 5 * 60 * 1000, // Refresh in background after 5 minutes
      priority: 'stale-while-revalidate'
    },
    quotes: {
      name: 'quotes',
      maxAge: 60 * 60 * 1000, // 1 hour for quotes
      refreshThreshold: 30 * 60 * 1000, // Refresh in background after 30 minutes
      priority: 'cache-first'
    },
    dashboard: {
      name: 'dashboard',
      maxAge: 2 * 60 * 1000, // 2 minutes for dashboard data
      refreshThreshold: 60 * 1000, // Refresh after 1 minute
      priority: 'stale-while-revalidate'
    }
  };

  // Generic cached data retrieval
  async getCachedData<T>(
    key: string,
    serverFetcher: () => Promise<T>,
    options: CacheOptions = {}
  ): Promise<CacheResult<T>> {
    const strategy = this.strategies[key] || this.strategies.tasks;
    const maxAge = options.maxAge || strategy.maxAge;
    const now = Date.now();

    try {
      // Check if we should force refresh
      if (options.forceRefresh) {
        const data = await serverFetcher();
        await this.updateCache(key, data, now);
        return {
          data,
          source: 'server',
          timestamp: now,
          stale: false
        };
      }

      // Get cached data and metadata
      const cachedData = await this.getCacheData<T>(key);
      const metadata = await indexedDBService.getMetadata(key);

      if (cachedData && metadata) {
        const age = now - metadata.lastUpdated;
        const isStale = age > maxAge;
        const shouldRefreshInBackground = age > strategy.refreshThreshold;

        // Handle different cache strategies
        switch (strategy.priority) {
          case 'cache-first':
            if (!isStale) {
              if (shouldRefreshInBackground && navigator.onLine) {
                this.refreshInBackground(key, serverFetcher);
              }
              return {
                data: cachedData,
                source: 'cache',
                timestamp: metadata.lastUpdated,
                stale: false
              };
            }
            break;

          case 'stale-while-revalidate':
            if (cachedData) {
              if (shouldRefreshInBackground && navigator.onLine) {
                this.refreshInBackground(key, serverFetcher);
              }
              return {
                data: cachedData,
                source: 'cache',
                timestamp: metadata.lastUpdated,
                stale: isStale
              };
            }
            break;

          case 'server-first':
            if (!isStale && navigator.onLine) {
              try {
                const freshData = await serverFetcher();
                await this.updateCache(key, freshData, now);
                return {
                  data: freshData,
                  source: 'server',
                  timestamp: now,
                  stale: false
                };
              } catch (error) {
                console.warn(`Server fetch failed for ${key}, using cache:`, error);
                if (cachedData) {
                  return {
                    data: cachedData,
                    source: 'cache',
                    timestamp: metadata.lastUpdated,
                    stale: true
                  };
                }
              }
            } else if (cachedData) {
              return {
                data: cachedData,
                source: 'cache',
                timestamp: metadata.lastUpdated,
                stale: isStale
              };
            }
            break;
        }
      }

      // No valid cache, fetch from server
      if (navigator.onLine) {
        try {
          const data = await serverFetcher();
          await this.updateCache(key, data, now);
          return {
            data,
            source: 'server',
            timestamp: now,
            stale: false
          };
        } catch (error) {
          // If server fails and we have fallback cache
          if (options.fallbackToCache && cachedData) {
            console.warn(`Server failed for ${key}, using stale cache:`, error);
            return {
              data: cachedData,
              source: 'cache',
              timestamp: metadata?.lastUpdated || 0,
              stale: true
            };
          }
          throw error;
        }
      } else {
        // Offline mode
        if (cachedData) {
          return {
            data: cachedData,
            source: 'cache',
            timestamp: metadata?.lastUpdated || 0,
            stale: true
          };
        }
        throw new Error(`No cached data available for ${key} and device is offline`);
      }

    } catch (error) {
      console.error(`Cache manager error for ${key}:`, error);
      throw error;
    }
  }

  // Cached task operations
  async getCachedTasks(filters?: any, options: CacheOptions = {}): Promise<CacheResult<Task[]>> {
    const user = await getCurrentUser();
    const cacheKey = `tasks_${user?.role || 'anonymous'}_${JSON.stringify(filters || {})}`;

    return this.getCachedData(
      cacheKey,
      () => backgroundSyncService.getCachedTasks(filters),
      { ...options, fallbackToCache: true }
    );
  }

  async getCachedTasksByShipment(shipmentId: string, options: CacheOptions = {}): Promise<CacheResult<Task[]>> {
    const cacheKey = `tasks_shipment_${shipmentId}`;
    
    return this.getCachedData(
      cacheKey,
      async () => {
        const tasks = await backgroundSyncService.getCachedTasks({ shipmentId });
        return tasks;
      },
      { ...options, fallbackToCache: true }
    );
  }

  async getCachedTasksByWorker(workerId: string, options: CacheOptions = {}): Promise<CacheResult<Task[]>> {
    const cacheKey = `tasks_worker_${workerId}`;
    
    return this.getCachedData(
      cacheKey,
      async () => {
        const tasks = await indexedDBService.getTasksAssignedToWorker(workerId);
        return tasks;
      },
      { ...options, fallbackToCache: true }
    );
  }

  // Cached shipment operations
  async getCachedShipments(filters?: any, options: CacheOptions = {}): Promise<CacheResult<Shipping[]>> {
    const user = await getCurrentUser();
    const cacheKey = `shipments_${user?.role || 'anonymous'}_${JSON.stringify(filters || {})}`;

    return this.getCachedData(
      cacheKey,
      () => backgroundSyncService.getCachedShipments(filters),
      { ...options, fallbackToCache: true }
    );
  }

  async getCachedShipmentsByClient(clientEmail: string, options: CacheOptions = {}): Promise<CacheResult<Shipping[]>> {
    const cacheKey = `shipments_client_${clientEmail}`;
    
    return this.getCachedData(
      cacheKey,
      async () => {
        const shipments = await indexedDBService.getShipmentsByClient(clientEmail);
        return shipments;
      },
      { ...options, fallbackToCache: true }
    );
  }

  // Dashboard data caching
  async getCachedDashboardData(options: CacheOptions = {}): Promise<CacheResult<any>> {
    const user = await getCurrentUser();
    const cacheKey = `dashboard_${user?.role || 'anonymous'}_${user?._id || 'anonymous'}`;

    return this.getCachedData(
      cacheKey,
      async () => {
        // Aggregate dashboard data
        const tasks = await backgroundSyncService.getCachedTasks();
        const shipments = await backgroundSyncService.getCachedShipments();
        const taskStats = await indexedDBService.getTaskStats();
        
        return {
          tasks: tasks.slice(0, 10), // Recent tasks
          shipments: shipments.slice(0, 10), // Recent shipments
          stats: taskStats,
          lastUpdated: Date.now()
        };
      },
      { ...options, maxAge: 2 * 60 * 1000 } // 2 minutes for dashboard
    );
  }

  // Quote data caching
  async getCachedQuotes(options: CacheOptions = {}): Promise<CacheResult<any[]>> {
    return this.getCachedData(
      'quotes',
      async () => {
        const quotes = await indexedDBService.getQuotes();
        return quotes;
      },
      { ...options, fallbackToCache: true }
    );
  }

  async getCachedFinalQuotes(options: CacheOptions = {}): Promise<CacheResult<any[]>> {
    return this.getCachedData(
      'final_quotes',
      async () => {
        const quotes = await indexedDBService.getFinalQuotes();
        return quotes;
      },
      { ...options, fallbackToCache: true }
    );
  }

  // Intelligent preloading based on user patterns
  async preloadUserData(): Promise<void> {
    const user = await getCurrentUser();
    if (!user) return;

    try {
      // Preload based on user role
      if (user.role === 'admin') {
        // Admin users typically need all data
        this.preloadInBackground('tasks', () => backgroundSyncService.getCachedTasks());
        this.preloadInBackground('shipments', () => backgroundSyncService.getCachedShipments());
        this.preloadInBackground('quotes', () => indexedDBService.getQuotes());
      } else if (user.role === 'worker') {
        // Workers need their assigned tasks and related shipments
        this.preloadInBackground('worker_tasks', () => 
          indexedDBService.getTasksAssignedToWorker(user._id.$oid)
        );
        this.preloadInBackground('worker_shipments', () => 
          backgroundSyncService.getCachedShipments()
        );
      } else {
        // Regular users need their shipments and updates
        this.preloadInBackground('user_shipments', () => 
          backgroundSyncService.getCachedShipments()
        );
      }
    } catch (error) {
      console.warn('Preload failed:', error);
    }
  }

  // Cache invalidation
  async invalidateCache(keys: string | string[]): Promise<void> {
    const keysArray = Array.isArray(keys) ? keys : [keys];
    
    for (const key of keysArray) {
      try {
        await indexedDBService.delete('metadata', key);
        // Note: We don't delete the actual data to allow offline access
        // Instead, we mark it as expired by removing metadata
      } catch (error) {
        console.warn(`Failed to invalidate cache for ${key}:`, error);
      }
    }
  }

  // Intelligent cache warming
  async warmCache(): Promise<void> {
    if (!navigator.onLine) return;

    try {
      // Warm frequently accessed data
      await backgroundSyncService.syncData({ background: true });
      
      // Preload user-specific data
      await this.preloadUserData();
      
    } catch (error) {
      console.warn('Cache warming failed:', error);
    }
  }

  // Cache optimization
  async optimizeCache(): Promise<{
    cleaned: number;
    compressed: number;
    errors: string[];
  }> {
    const result = {
      cleaned: 0,
      compressed: 0,
      errors: [] as string[]
    };

    try {
      // Remove expired entries
      const allMetadata = await indexedDBService.getAll<any>('metadata');
      const now = Date.now();
      
      for (const metadata of allMetadata) {
        if (metadata.expiresAt < now) {
          await indexedDBService.delete('metadata', metadata.key);
          result.cleaned++;
        }
      }

      // TODO: Implement data compression for large datasets
      
    } catch (error) {
      result.errors.push(error instanceof Error ? error.message : 'Optimization failed');
    }

    return result;
  }

  // Cache statistics
  async getCacheStats(): Promise<{
    totalSize: number;
    entries: number;
    hitRate: number;
    lastOptimized: number;
    storage: {
      tasks: number;
      shipments: number;
      quotes: number;
      metadata: number;
    };
  }> {
    try {
      const tasks = await indexedDBService.getTasks();
      const shipments = await indexedDBService.getShipments();
      const quotes = await indexedDBService.getQuotes();
      const metadata = await indexedDBService.getAll<any>('metadata');

      // Rough size estimation (this is simplified)
      const taskSize = JSON.stringify(tasks).length;
      const shipmentSize = JSON.stringify(shipments).length;
      const quoteSize = JSON.stringify(quotes).length;
      const metadataSize = JSON.stringify(metadata).length;

      return {
        totalSize: taskSize + shipmentSize + quoteSize + metadataSize,
        entries: tasks.length + shipments.length + quotes.length,
        hitRate: 0.85, // This would be calculated from actual usage metrics
        lastOptimized: Date.now(), // This would come from stored optimization data
        storage: {
          tasks: taskSize,
          shipments: shipmentSize,
          quotes: quoteSize,
          metadata: metadataSize
        }
      };
      
    } catch (error) {
      console.error('Failed to get cache stats:', error);
      return {
        totalSize: 0,
        entries: 0,
        hitRate: 0,
        lastOptimized: 0,
        storage: {
          tasks: 0,
          shipments: 0,
          quotes: 0,
          metadata: 0
        }
      };
    }
  }

  // Private helper methods
  private async getCacheData<T>(key: string): Promise<T | null> {
    // This would map to the appropriate store based on key
    if (key.includes('tasks')) {
      const tasks = await indexedDBService.getTasks();
      return tasks as unknown as T;
    } else if (key.includes('shipments')) {
      const shipments = await indexedDBService.getShipments();
      return shipments as unknown as T;
    } else if (key.includes('quotes')) {
      const quotes = await indexedDBService.getQuotes();
      return quotes as unknown as T;
    }
    
    // Default to generic get
    return indexedDBService.get<T>('metadata', key);
  }

  private async updateCache<T>(key: string, data: T, timestamp: number): Promise<void> {
    // Update the appropriate store based on key
    if (key.includes('tasks') && Array.isArray(data)) {
      await indexedDBService.putTasks(data as unknown as Task[]);
    } else if (key.includes('shipments') && Array.isArray(data)) {
      await indexedDBService.putShipments(data as unknown as Shipping[]);
    } else if (key.includes('quotes') && Array.isArray(data)) {
      await indexedDBService.putQuotes(data as unknown as any[]);
    }
    
    // Always update metadata
    await indexedDBService.updateMetadata(key, timestamp);
  }

  private async refreshInBackground<T>(
    key: string,
    fetcher: () => Promise<T>
  ): Promise<void> {
    try {
      const data = await fetcher();
      await this.updateCache(key, data, Date.now());
    } catch (error) {
      console.warn(`Background refresh failed for ${key}:`, error);
    }
  }

  private async preloadInBackground<T>(
    key: string,
    fetcher: () => Promise<T>
  ): Promise<void> {
    setTimeout(async () => {
      try {
        const data = await fetcher();
        await this.updateCache(key, data, Date.now());
      } catch (error) {
        console.warn(`Preload failed for ${key}:`, error);
      }
    }, 1000); // Delay to not block main operations
  }

  // Cleanup
  async clearAllCache(): Promise<void> {
    await indexedDBService.clearAllData();
  }
}

// Singleton instance
const cacheManager = new CacheManagerService();

export default cacheManager;
export { CacheManagerService };
export type { CacheOptions, CacheResult, CacheStrategy };
